import { supabase } from '../lib/supabase'
import { logError, logInfo } from '../utils/logger'
import { prepareImageForUpload } from '../utils/imageUploadPrep'

export interface UserImageAsset {
  storagePath: string
  signedUrl: string
  width?: number
  height?: number
  mimeType?: string
  sizeBytes?: number
  uploadedAt?: string
  originalFileName?: string
}

interface UploadUserImageResponse {
  success: boolean
  data?: UserImageAsset
  error?: string
}

interface ListUserImagesResponse {
  success: boolean
  data?: {
    images: UserImageAsset[]
  }
  error?: string
}

interface SignUserImagesResponse {
  success: boolean
  data?: {
    images: Array<{
      storagePath: string
      signedUrl: string
      expiresAt: string
    }>
  }
  error?: string
}

interface DeleteUserImageResponse {
  success: boolean
  error?: string
}

const parseResponseBody = async <T>(
  response: Response
): Promise<{
  data: T | null
  fallbackError?: string
}> => {
  const rawBody = await response.text()
  if (!rawBody) {
    return { data: null }
  }

  try {
    return { data: JSON.parse(rawBody) as T }
  } catch (error) {
    logError('userImages', 'Failed to parse function response', {
      status: response.status,
      error,
      rawBody,
    })

    return {
      data: null,
      fallbackError: rawBody,
    }
  }
}

const getAuthToken = async (): Promise<string | null> => {
  const { data, error } = await supabase.auth.getSession()
  if (error) {
    logError('userImages', 'Failed to get session', { error })
    return null
  }
  return data.session?.access_token || null
}

const getUserId = async (): Promise<string | null> => {
  const { data, error } = await supabase.auth.getUser()
  if (error) {
    logError('userImages', 'Failed to get user', { error })
    return null
  }
  return data.user?.id || null
}

export async function uploadUserImage(
  file: File
): Promise<{ success: boolean; data?: UserImageAsset; error?: string }> {
  try {
    const token = await getAuthToken()
    const userId = await getUserId()

    if (!token || !userId) {
      return { success: false, error: 'User is not authenticated' }
    }

    // IMPORTANT: We send base64 JSON to a Netlify Function. Large images can exceed Netlify request
    // body limits before our function ever runs. Prepare the image (downscale/compress) first.
    const prepared = await prepareImageForUpload(file)
    const fileData = prepared.dataUrl

    const response = await fetch('/.netlify/functions/upload-user-image', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        fileName: prepared.file.name,
        fileData,
        mimeType: prepared.file.type,
        userId,
      }),
    })

    const { data: result, fallbackError } =
      await parseResponseBody<UploadUserImageResponse>(response)

    if (!response.ok || !result?.success || !result?.data) {
      return {
        success: false,
        error: result?.error || fallbackError || 'Failed to upload image',
      }
    }

    logInfo('userImages', 'Image uploaded', {
      storagePath: result.data.storagePath,
      sizeBytes: result.data.sizeBytes,
    })

    return { success: true, data: result.data }
  } catch (error) {
    logError('userImages', 'Upload failed', { error })
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed',
    }
  }
}

export async function listUserImages(): Promise<{
  success: boolean
  data?: UserImageAsset[]
  error?: string
}> {
  try {
    const token = await getAuthToken()
    if (!token) {
      return { success: false, error: 'User is not authenticated' }
    }

    const response = await fetch('/.netlify/functions/list-user-images', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })

    const { data: result, fallbackError } =
      await parseResponseBody<ListUserImagesResponse>(response)

    if (!response.ok || !result?.success || !result.data) {
      return {
        success: false,
        error: result?.error || fallbackError || 'Failed to list images',
      }
    }

    return { success: true, data: result.data.images }
  } catch (error) {
    logError('userImages', 'List failed', { error })
    return {
      success: false,
      error: error instanceof Error ? error.message : 'List failed',
    }
  }
}

export async function signUserImages(storagePaths: string[]): Promise<{
  success: boolean
  data?: Record<string, string>
  error?: string
}> {
  try {
    const token = await getAuthToken()
    if (!token) {
      return { success: false, error: 'User is not authenticated' }
    }

    const response = await fetch('/.netlify/functions/sign-user-images', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ storagePaths }),
    })

    const { data: result, fallbackError } =
      await parseResponseBody<SignUserImagesResponse>(response)

    if (!response.ok || !result?.success || !result.data) {
      return {
        success: false,
        error: result?.error || fallbackError || 'Failed to sign images',
      }
    }

    const map: Record<string, string> = {}
    for (const image of result.data.images) {
      map[image.storagePath] = image.signedUrl
    }

    return { success: true, data: map }
  } catch (error) {
    logError('userImages', 'Sign failed', { error })
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Sign failed',
    }
  }
}

export async function deleteUserImage(storagePath: string): Promise<{
  success: boolean
  error?: string
}> {
  try {
    const token = await getAuthToken()
    if (!token) {
      return { success: false, error: 'User is not authenticated' }
    }

    const response = await fetch('/.netlify/functions/delete-user-image', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ storagePath }),
    })

    const { data: result, fallbackError } =
      await parseResponseBody<DeleteUserImageResponse>(response)

    if (!response.ok || !result?.success) {
      return {
        success: false,
        error: result?.error || fallbackError || 'Failed to delete image',
      }
    }

    return { success: true }
  } catch (error) {
    logError('userImages', 'Delete failed', { error })
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Delete failed',
    }
  }
}
